import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, MessageSquare, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EVENTS = [
  "deployment.created",
  "deployment.ready",
  "deployment.failed",
  "deployment.cancelled",
  "deployment.skipped",
  "deployment.promoted",
  "deployment.rolled_back",
  "release.started",
  "release.completed",
  "release.aborted",
] as const;

interface Integration {
  id: string;
  provider: "slack" | "discord";
  events: string[];
  active: boolean;
  failureCount: number;
  lastError: string | null;
  lastSuccessAt: string | null;
  broken: boolean;
}

/**
 * 006 US8 — Slack/Discord deploy notifications (rendered on the Webhooks
 * tab). Paste an incoming-webhook URL; a test message validates it at
 * save time; the URL is never shown again after creation.
 */
export function ChatIntegrations({ orgSlug, projectSlug }: { orgSlug: string; projectSlug: string }) {
  const qc = useQueryClient();
  const [provider, setProvider] = useState<"slack" | "discord">("slack");
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<string[]>(["deployment.ready", "deployment.failed"]);

  const q = useQuery({
    queryKey: ["integrations", orgSlug, projectSlug],
    queryFn: () =>
      api
        .get<{ integrations: Integration[] }>(`/orgs/${orgSlug}/projects/${projectSlug}/integrations`)
        .then((r) => r.integrations),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post(`/orgs/${orgSlug}/projects/${projectSlug}/integrations`, { provider, webhookUrl: url, events }),
    onSuccess: () => {
      setUrl("");
      qc.invalidateQueries({ queryKey: ["integrations", orgSlug, projectSlug] });
    },
  });
  const patch = useMutation({
    mutationFn: (v: { id: string; active?: boolean; events?: string[] }) =>
      api.patch(`/orgs/${orgSlug}/projects/${projectSlug}/integrations/${v.id}`, {
        ...(v.active !== undefined ? { active: v.active } : {}),
        ...(v.events ? { events: v.events } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations", orgSlug, projectSlug] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/orgs/${orgSlug}/projects/${projectSlug}/integrations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations", orgSlug, projectSlug] }),
  });

  return (
    <div className="of-card">
      <div className="of-card-header">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" /> Chat notifications
        </h3>
        <span className="hidden sm:inline text-2xs text-subtle-foreground">Slack / Discord incoming webhooks · no payload parsing needed</span>
      </div>

      {q.isLoading && <div className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</div>}
      {!q.isLoading && (q.data?.length ?? 0) === 0 && (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">
          Not connected. Paste a Slack or Discord incoming-webhook URL below — you'll get a test message immediately.
        </div>
      )}

      {q.data?.map((integ) => (
        <div key={integ.id} className="of-row gap-3 items-start">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold capitalize">{integ.provider}</span>
              {!integ.active && (
                <span className="text-2xs uppercase tracking-wider bg-subtle border border-border-muted rounded px-1.5 py-0.5">
                  paused
                </span>
              )}
              {integ.broken && (
                <span className="inline-flex flex-wrap min-w-0 items-center gap-1 text-2xs text-danger">
                  <AlertTriangle className="h-3 w-3" /> delivery failing ({integ.failureCount}×)
                  {integ.lastError ? ` — ${integ.lastError}` : ""}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1 mt-1">
              {EVENTS.map((e) => {
                const on = integ.events.includes(e);
                return (
                  <button
                    key={e}
                    onClick={() =>
                      patch.mutate({
                        id: integ.id,
                        events: on ? integ.events.filter((x) => x !== e) : [...integ.events, e],
                      })
                    }
                    className={
                      "text-2xs font-mono rounded px-1.5 py-0.5 border transition-colors " +
                      (on
                        ? "bg-accent-subtle text-accent border-accent/30"
                        : "bg-subtle text-subtle-foreground border-border-muted hover:text-foreground")
                    }
                    title={on ? "Click to stop notifying on this event" : "Click to notify on this event"}
                  >
                    {e.replace("deployment.", "")}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => patch.mutate({ id: integ.id, active: !integ.active })}
              disabled={patch.isPending}
            >
              {integ.active ? "Pause" : "Resume"}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Remove integration"
              onClick={() => {
                if (window.confirm(`Remove the ${integ.provider} integration?`)) del.mutate(integ.id);
              }}
            >
              {del.isPending && del.variables === integ.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5 text-danger" />
              )}
            </Button>
          </div>
        </div>
      ))}

      <div className="px-4 py-3 border-t border-border-muted space-y-2">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs">Provider</Label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value as "slack" | "discord")}
              className="h-8 px-2 rounded-md border border-border bg-background text-sm font-mono"
            >
              <option value="slack">slack</option>
              <option value="discord">discord</option>
            </select>
          </div>
          <div className="space-y-1 flex-1 min-w-[240px]">
            <Label className="text-xs">Incoming webhook URL</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={provider === "slack" ? "https://hooks.slack.com/services/…" : "https://discord.com/api/webhooks/…"}
              className="h-8 font-mono text-xs"
            />
          </div>
          <Button variant="default" size="sm" disabled={!url || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Connect
          </Button>
        </div>
        <div className="flex flex-wrap gap-1">
          {EVENTS.map((e) => {
            const on = events.includes(e);
            return (
              <button
                key={e}
                onClick={() => setEvents((cur) => (on ? cur.filter((x) => x !== e) : [...cur, e]))}
                className={
                  "text-2xs font-mono rounded px-1.5 py-0.5 border transition-colors " +
                  (on
                    ? "bg-accent-subtle text-accent border-accent/30"
                    : "bg-subtle text-subtle-foreground border-border-muted hover:text-foreground")
                }
              >
                {e.replace("deployment.", "")}
              </button>
            );
          })}
        </div>
        {create.error instanceof ApiError && (
          <div className="text-xs text-danger">{create.error.message}</div>
        )}
      </div>
    </div>
  );
}
